import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DashFeaturesEnum } from "dash-common";
import {
  Button,
  FilterContext,
  Flex,
  InputSearch,
  ModalContext,
  SearchContext,
  Select,
  useCheckIfSubscriptionFeatureActiveBroker,
  useScreenResolution,
} from "dash-ui";
import {
  PORTFOLIO_SORT_VALUES,
  PortfolioSortFields,
  PortfolioSortOptionsEnum,
} from "./PortfoliosPageFilters.constants";
import styled from "styled-components";

export const SelectContainer = styled(Flex)`
  width: 100%;
  visibility: visible;
`;

const PortfoliosPageTopBar = () => {
  const { t } = useTranslation("pages");
  const { setSearchSlug } = useContext(SearchContext);
  const { toggleModal } = useContext(ModalContext);
  const { setFilterState, state: filterState } = useContext(FilterContext);
  const [searchValue, setSearchValue] = useState("");
  const {
    devices: { isMobile },
  } = useScreenResolution();
  const { checkFeatureAccess } = useCheckIfSubscriptionFeatureActiveBroker([
    DashFeaturesEnum.PORTFOLIO_CREATION_AND_SHARING,
  ]);
  const refSearch = useRef<HTMLInputElement>(null);

  const setSortOptions = (value: PortfolioSortOptionsEnum) => {
    let sortOptions: PortfolioSortFields[];
    let defaultValue: PortfolioSortOptionsEnum | undefined;

    if (!value) {
      sortOptions = [
        PORTFOLIO_SORT_VALUES[PortfolioSortOptionsEnum.AlphabeticalAZ],
      ];
    } else {
      sortOptions = [PORTFOLIO_SORT_VALUES[value]];
      defaultValue = value;
    }

    setFilterState("sort", { sortOptions, defaultValue });
  };

  const handleSortChanged = useCallback(
    (value) => {
      setSortOptions(value);
    },
    [setSortOptions]
  );

  const memoizedSortOptions = useMemo(() => {
    return Object.values(PortfolioSortOptionsEnum).map((item) => ({
      label: t(`common:sortOptions.${item}`),
      value: item,
    }));
  }, [t]);

  const memoizedSelectComponents = useMemo(() => {
    return isMobile
      ? {
          SelectContainer: ({ children }: { children: React.ReactNode }) => {
            return (
              <Flex>
                <SelectContainer w="100%">{children}</SelectContainer>
              </Flex>
            );
          },
        }
      : {};
  }, [isMobile]);

  return (
    <Flex
      flexDirection={isMobile ? "column" : "row"}
      justifyContent="space-between"
      alignItems="center"
      mb={6}
    >
      <Flex w={isMobile ? "100%" : 350}>
        <InputSearch
          ref={refSearch}
          placeholder={t("portfoliosPage.portfoliosSearchPlaceholder") || ""}
          w="100%"
          value={searchValue}
          onClear={() => {
            setSearchValue("");
            setSearchSlug("");
          }}
          onChange={(e) => {
            setSearchSlug(e.target.value);
            setSearchValue(e.target.value);
          }}
        />
      </Flex>

      <Flex
        justifyContent="flex-end"
        w="100%"
        flexDirection={isMobile ? "column" : "row"}
      >
        <Flex mt={2} w={isMobile ? "100%" : 200}>
          <Select
            w="100%"
            withCheckbox
            placeholder={t("portfoliosPage.sortBy")}
            value={filterState?.sort?.defaultValue}
            isClearable
            isSearchable={false}
            valueContainerMaxWidth="230px"
            options={memoizedSortOptions}
            onChanged={handleSortChanged}
            components={memoizedSelectComponents}
          />
        </Flex>

        <Flex
          ml={2}
          mt={2}
          w={isMobile ? "100%" : "auto"}
          justifyContent="center"
        >
          <Button
            icon={isMobile ? undefined : "plus"}
            minWidth={isMobile ? "50vw" : 150}
            px={[0, 4, 4, 4]}
            onClick={() => {
              if (checkFeatureAccess()) {
                toggleModal(true, "AddPortfolioFolder");
              }
            }}
          >
            {t("portfoliosPage.addNew")}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default PortfoliosPageTopBar;
